/**
 * כפתור נגישות צף — TASKS 4.6 · PRD 12.5.
 *
 * ⛔ **מימוש עצמי, לא סקריפט חיצוני של ספק נגישות** (PRD 12.5: "אפס עלות,
 *    אפס תלות בצד שלישי, אפס פגיעה בזמן הטעינה"). ⚠️ ⚠️ **כפתור נגישות
 *    אינו הופך אתר לא-נגיש לנגיש** — הוא שכבת נוחות מעל בסיס שכבר נבנה
 *    נגיש מהיסוד (4.1–4.5). ראו האזהרה המלאה ב-PRD 12.5.
 *
 * מלכודת הפוקוס/`Esc`/החזרת הפוקוס כאן מבוססים על אותו דפוס בדיוק כמו
 * `Lightbox.tsx` (4.2) — ⛔ לא ספרייה חיצונית, שני המימושים העצמאיים
 * היחידים באתר לדיאלוג נגיש.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { X } from '@phosphor-icons/react';
import { useAccessibility, type A11yFontSize, type A11yToggleKey } from './AccessibilityContext.js';

const FONT_SIZES: readonly A11yFontSize[] = [100, 125, 150, 175];

const TOGGLES: ReadonlyArray<{ key: A11yToggleKey; label: string }> = [
  { key: 'highContrast', label: 'ניגודיות גבוהה' },
  { key: 'grayscale', label: 'גווני אפור' },
  { key: 'emphasizeLinks', label: 'הדגשת קישורים' },
  { key: 'readableFont', label: 'פונט קריא' },
  { key: 'stopAnimations', label: 'עצירת אנימציות' },
];

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * אייקון "Universal Access" — הסמל המוכר שרוב תוספי הנגישות משתמשים בו (אדם
 * בתוך עיגול). ⚠️ ⛔ **הגרסה הקודמת כאן הייתה path גיאומטרי מגובש ידנית
 * שיצא שבור ויזואלית** ("נראה כמו באג") — הוחלף ב-path מדויק ומוכר, מקור:
 * Font Awesome Free `universal-access` (CC BY 4.0, Fonticons Inc.),
 * https://fontawesome.com/icons/universal-access — ⭐ **מוטמע סטטית כאן**,
 * ⛔ אין טעינת סקריפט/פונט/גופן חיצוני בזמן ריצה, אין קריאת רשת, אין תלות
 * בחבילת npm — עדיין "אפס תלות בצד שלישי" (PRD 12.5), רק ה**איור** שאול
 * ומיוחס במקום מגובש-ידנית-ובאג.
 */
function AccessibilityIcon() {
  return (
    <svg viewBox="0 0 512 512" width="24" height="24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm161.5-86.1c-12.2-5.2-26.3 .4-31.5 12.6s.4 26.3 12.6 31.5l11.9 5.1c17.3 7.4 35.2 12.9 53.6 16.3l0 50.1c0 4.3-.7 8.6-2.1 12.6l-28.7 86.1c-4.2 12.6 2.6 26.2 15.2 30.4s26.2-2.6 30.4-15.2l24.4-73.2c1.3-3.8 4.8-6.4 8.8-6.4s7.6 2.6 8.8 6.4l24.4 73.2c4.2 12.6 17.8 19.4 30.4 15.2s19.4-17.8 15.2-30.4l-28.7-86.1c-1.4-4.1-2.1-8.3-2.1-12.6l0-50.1c18.4-3.5 36.3-8.9 53.6-16.3l11.9-5.1c12.2-5.2 17.8-19.3 12.6-31.5s-19.3-17.8-31.5-12.6L338.7 175c-26.1 11.2-54.2 17-82.7 17s-56.5-5.8-82.7-17l-11.9-5.1zM256 160a40 40 0 1 0 0-80 40 40 0 1 0 0 80z"
      />
    </svg>
  );
}

export function AccessibilityWidget() {
  const { prefs, setFontSize, toggle, reset } = useAccessibility();
  const [isOpen, setIsOpen] = useState(false);
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  function open() {
    setIsOpen(true);
  }

  /**
   * ⚠️ ⛔ **תמיד חוזר ל-`triggerRef`, ⛔ לא ל"מה שהיה ממוקד קודם"** — בשונה
   *    מ-`Lightbox.tsx` (4.2), ש-`onClose` שלו יכול להיפתח מכמה תמונות
   *    שונות. כאן יש כפתור טריגר יחיד, וזה בדיוק הניסוח המילולי ב-PRD 12.5:
   *    "הפוקוס חוזר **לכפתור**". ⚠️ זה גם התיקון לבאג אמיתי: לחיצת עכבר על
   *    הכפתור לא ממקדת אותו תמיד לפני `onClick` (jsdom אף פעם לא, וגם לא
   *    כל דפדפן/פלטפורמה עקבי בזה) — `document.activeElement` באותו רגע
   *    יכול להיות `<body>`, לא הכפתור, מה שהיה מחזיר את הפוקוס למקום הלא-נכון.
   */
  function close() {
    setIsOpen(false);
    triggerRef.current?.focus();
  }

  // ⭐ פוקוס נכנס לדיאלוג בפתיחה — אחרי שהוא נרנדר בפועל
  useEffect(() => {
    if (isOpen) dialogRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) return;
        const first = focusable[0] as HTMLElement;
        const last = focusable[focusable.length - 1] as HTMLElement;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // ⚠️ תלוי בכוונה רק ב-isOpen — `close`/`dialogRef` נגזרים מ-refs/state
    //    יציבים, ורישום מחדש בכל רינדור (כמו ב-Lightbox.tsx, 4.2) לא נחוץ.
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="a11y-fab"
        aria-label="הגדרות נגישות"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => (isOpen ? close() : open())}
      >
        <AccessibilityIcon />
      </button>

      {isOpen && (
        <div
          className="a11y-panel-overlay"
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="a11y-panel"
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15 }}
          >
            <div className="flex items-center justify-between">
              <h2 id={titleId} className="text-lg font-bold">
                הגדרות נגישות
              </h2>
              <button type="button" className="a11y-panel-close" onClick={close} aria-label="סגירה">
                <X size={18} weight="regular" aria-hidden="true" />
              </button>
            </div>

            <div className="mt-4">
              <span className="block text-sm font-semibold">הגדלת טקסט</span>
              <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="הגדלת טקסט">
                {FONT_SIZES.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className="a11y-toggle-btn"
                    aria-pressed={prefs.fontSize === size}
                    onClick={() => setFontSize(size)}
                  >
                    {size}%
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              {TOGGLES.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className="a11y-toggle-btn"
                  aria-pressed={prefs[key]}
                  onClick={() => toggle(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
              <button type="button" className="btn btn-secondary" onClick={reset}>
                איפוס
              </button>
              <Link to="/accessibility" className="text-link underline" onClick={close}>
                הצהרת הנגישות
              </Link>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
